import React, { useEffect, Suspense, lazy } from "react";
import {BrowserRouter as Router, Route, Routes, Navigate, useLocation, NavLink} from "react-router-dom";
import {AuthProvider} from "./contexts/AuthContext";
import "./Css/Styles.css";
import "./Css/Overrides.css";
import { ConfirmationServiceProvider } from './contexts/ConfirmationServiceContext';

import {PrivateRoute} from "./Components/Auth/PrivateRoute";
import pathImage from "./img/path.png";
import { trackPageView } from './utils/analytics';
import SimpleLoader from './Components/SimpleLoader';
import imagePreloader from './utils/imagePreloader';
import { initializePerformanceOptimizations } from './utils/performanceOptimization';

// Check if we're in chat-only mode
const isChatOnlyMode = window.location.pathname === '/chat' || window.location.search.includes('agentId=');

// Lazy load heavy components only when not in chat-only mode
const Login = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/Auth/Login'));
const Register = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/Auth/Register'));
const Studies = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/Studies'));
const Settings = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/Settings'));
const Logout = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/Logout'));
const StudyDashboard = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/StudyDashboard'));
const CreateStudy = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/CreateStudy'));
const Chat = lazy(() => import('./Components/Chat'));
const AgentPreview = lazy(() => isChatOnlyMode ? Promise.resolve({ default: SimpleLoader }) : import('./Components/AgentPreview'));
const Analyser = lazy(() => import('./Components/Analyser')); // Temporarily disabled to reduce bundle size
const StudyUnavailable = lazy(() => import('./Components/StudyUnavailable'));

function App() {
  
  // Initialize performance optimizations on app start
  useEffect(() => {
    initializePerformanceOptimizations();
  }, []);

  // useEffect(() => {
  //   const script = document.createElement('script');
  //   script.src = '//cdn.jsdelivr.net/npm/eruda';
  //   script.async = true;
  //   script.onload = () => {
  //     window.eruda.init();
  //     console.log("Eruda initialized for mobile debugging");
  //   };
  //   document.body.appendChild(script);
  // }, []);

  // If we're in chat-only mode, render minimal structure
  if (isChatOnlyMode) {
    return (
      <AuthProvider>
        <Router>
          <Suspense fallback={<SimpleLoader />}>
            <Routes>
              <Route path="/chat" element={<Chat />} />
              <Route path="*" element={<Navigate to="/chat" />} />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={<SimpleLoader />}>
          <ConfirmationServiceProvider>
            <AppContent />
          </ConfirmationServiceProvider>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

const AppContent = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const isChatPage = location.pathname === "/chat";
  const isRegisterPage = location.pathname === "/register";
  const isStudyUnavailablePage = location.pathname === "/study-unavailable";
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  useEffect(() => {
    trackPageView(location.pathname);
    
    // Preload critical images based on route
    const urlParams = new URLSearchParams(location.search);
    const agentId = urlParams.get('agentId') || location.pathname.match(/\/agent-preview\/([^/]+)/)?.[1];
    
    imagePreloader.preloadCriticalImages(location.pathname, { agentId });
  }, [location]);


  return (
    <div className={`app-container ${isLoginPage ? "login-layout" : ""}`}>
      {(!isLoginPage && !isChatPage && !isRegisterPage && !isStudyUnavailablePage) && (
        <nav className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className='brand permanent-marker-regular'>
            {sidebarCollapsed ? 'DW' : <>Discovery<br/>Works</>}
          </div>
          <NavLink to="/" className={({isActive}) => (isActive ? "active" : "")}>
            <i className="pi pi-comments"></i> 
            {!sidebarCollapsed && <span>Studies</span>}
          </NavLink>
          {/* Analyser temporarily disabled to reduce bundle size */}
          <NavLink to="/analyser" className={({isActive}) => (isActive ? "active" : "")}>
            <i className="pi pi-chart-pie"></i> 
            {!sidebarCollapsed && <span>Analyser</span>}
          </NavLink>
          <NavLink to="/settings" className={({isActive}) => (isActive ? "active" : "")}>
            <i className="pi pi-cog"></i> 
            {!sidebarCollapsed && <span>Settings</span>}
          </NavLink>
          <NavLink to="/logout" className={({isActive}) => (isActive ? "active" : "")}>
            <i className="pi pi-sign-out"></i> 
            {!sidebarCollapsed && <span>Logout</span>}
          </NavLink>
          {!sidebarCollapsed && (
            <img src={pathImage} alt="Discoveryworks brand image, a dotted line leading to an x, referring to the journey of discovery that is research" />
          )}
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <i className={`pi ${sidebarCollapsed ? 'pi-angle-right' : 'pi-angle-left'}`}></i>
          </button>
        </nav>
      )}
      <div className={`content ${isLoginPage ? "login-content" : ""}`}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/" element={
            <PrivateRoute>
              <Studies />
            </PrivateRoute>
          } />
          <Route path="/studies/create" element={
            <PrivateRoute>
              <CreateStudy />
            </PrivateRoute>
          } />
          <Route path="/agent-preview/:agentId" element={
            <PrivateRoute>
              <AgentPreview />
            </PrivateRoute>
          } />
          <Route path="/studies/:studyId" element={
            <PrivateRoute>
              <StudyDashboard />
            </PrivateRoute>
          } />
          <Route path="/settings" element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          } />
          <Route path="/analyser" element={
            <PrivateRoute>
              <Analyser />
            </PrivateRoute>
          } />
          <Route path="/logout" element={
            <PrivateRoute>
              <Logout />
            </PrivateRoute>
          } />
          <Route path="/chat" element={<Chat />} />
          <Route path="/study-unavailable" element={<StudyUnavailable />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </div>
    </div>
  );
};

export default App;
